/* eslint-disable @typescript-eslint/no-explicit-any */
import { backOfficeAccess } from "@/features/auth/back-office-access";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { CompanyAvatar } from "@/components/molecules/company-avatar";
import { UserAvatar } from "@/components/molecules/user-avatar";

import {
  BuildingIcon,
  CalendarIcon,
  DeviceMobileIcon,
  CaretUpDownIcon,
  DotsThreeOutlineVerticalIcon,
  FileTextIcon,
  ClockCounterClockwiseIcon,
  GaugeIcon,
  GearIcon,
  MoonIcon,
  NotepadIcon,
  PlugsIcon,
  QuestionIcon,
  StackIcon,
  SunIcon,
  TagIcon,
  ToolboxIcon,
  TrayIcon,
  UserCircleIcon,
  UserIcon,
  UsersFourIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import * as React from "react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { getUserName, useUser } from "@/features/auth/hooks/use-user";
import {
  useMyClients,
  withoutDemoSpaces,
} from "@/features/clients/hooks/use-clients";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import { usePermissions } from "@/features/clients/hooks/use-permissions";
import { usePreferredCompany } from "@/features/clients/hooks/use-preferred-company";
import { useVoiceNumbers } from "@/features/connectors/hooks/use-voice-numbers";
import { useSidebarBadges } from "@/features/notifications/hooks/use-sidebar-badges";
import { FirstSteps } from "@/views/onboarding/first-steps";
import { useAvailableTools } from "@/features/tools/hooks/use-tools";
import { isDemoMode } from "@/features/utils/demo-mode";
import { useTranslation } from "@/features/utils/i18n";
import { useTheme } from "@/features/utils/theme";
import { AlarmIcon, SignOutIcon } from "@phosphor-icons/react/dist/ssr";
import { Link, useLocation, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { useCompanyPages } from "./company-settings/nav";

export function BackofficeSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation("sidebar");
  const { company: clientId } = useParams<{ company: string }>();
  const { preferredCompanyId } = usePreferredCompany();
  const permissions = usePermissions();
  const { data: availableTools = [] } = useAvailableTools();
  const isOwner = useHasRole("OWNER");

  // Use the URL company ID if available, otherwise fall back to preferred company
  const effectiveClientId = clientId || preferredCompanyId;

  // Helper to check permissions
  const hasPermission = (permission: string) =>
    permissions.includes(permission as any);

  // Helper to check if a tool is enabled
  const isToolEnabled = (toolName: string) => {
    const tool = availableTools.find((t) => t.tool.name === toolName);
    return tool?.clientTool?.enabled || false;
  };

  // Helper to get tool configuration
  const getToolConfig = (toolName: string, configKey: string) => {
    const tool = availableTools.find((t) => t.tool.name === toolName);
    return tool?.clientTool?.configuration?.[configKey];
  };

  // Check if calendar tool is enabled
  const calendarEnabled = isToolEnabled("booking");

  // Check if qualifications tool is enabled
  const qualificationsEnabled = isToolEnabled("qualifications");

  // Check if basic_tools is enabled with rag_search
  const ragSearchEnabled =
    isToolEnabled("basic_tools") &&
    getToolConfig("basic_tools", "enable_rag_search");

  // Check if the FAQ module is enabled
  const faqEnabled =
    isToolEnabled("faq") && getToolConfig("faq", "enable_faq") !== false;

  // Check if the Messagerie module is enabled. Demo mode serves its inbox from
  // fixtures, so it is shown whether or not the tenant behind the demo has it.
  const messagesEnabled = isDemoMode() || isToolEnabled("messages");

  // VoIP handsets are owner-only *and* pointless without a line to hang them
  // off: a handset must present a number the tenant owns (the back-office
  // refuses to create one otherwise), so with no Twilio or SIP number the page
  // can only ever show an empty table and a form that cannot be submitted.
  // Both halves are checked here rather than inside the page, so the entry
  // appears on its own the moment the first number lands.
  const { numbers: voiceNumbers } = useVoiceNumbers(effectiveClientId || "");
  const voipAccountsVisible = isOwner && voiceNumbers.length > 0;

  // Attention counters. Gated on exactly the same conditions as the menu items
  // they decorate, so a hidden entry never triggers its request.
  const badges = useSidebarBadges({
    clientId: effectiveClientId || "",
    submissions: hasPermission("qualifications:view") && qualificationsEnabled,
    faq: hasPermission("faq:view") && faqEnabled,
    messages: hasPermission("messages:view") && messagesEnabled,
    channels: hasPermission("settings:connectors"),
  });

  const companyPages = useCompanyPages(effectiveClientId || "");
  const { pathname } = useLocation();
  const inCompany = companyPages.some((page) => pathname.startsWith(page.url));

  // Dynamic menu based on whether we're in a company context
  const data = effectiveClientId
    ? {
        "": [
          hasPermission("dashboard:view") && {
            title: t("menu.dashboard"),
            url: `/${effectiveClientId}/home`,
            icon: GaugeIcon,
          },
        ].filter(Boolean),
        [t("sections.activity")]: [
          hasPermission("contacts:view") && {
            title: t("menu.contacts"),
            url: `/${effectiveClientId}/contacts`,
            icon: UsersFourIcon,
          },
          hasPermission("calendar:view") &&
            calendarEnabled && {
              title: t("menu.calendar"),
              url: `/${effectiveClientId}/calendar`,
              icon: CalendarIcon,
            },
          hasPermission("faq:view") &&
            faqEnabled && {
              title: t("menu.faq"),
              url: `/${effectiveClientId}/faq`,
              icon: QuestionIcon,
              badge: badges.faq,
              badgeTone: "warning" as const,
              badgeLabel: t("badges.faqNeedsRefinement", { count: badges.faq }),
            },
          hasPermission("messages:view") &&
            messagesEnabled && {
              title: t("menu.messages"),
              url: `/${effectiveClientId}/messages`,
              icon: TrayIcon,
              badge: badges.messages,
              badgeTone: "warning" as const,
              badgeLabel: t("badges.messagesPending", {
                count: badges.messages,
              }),
            },
          hasPermission("qualifications:view") &&
            qualificationsEnabled && {
              title: t("menu.submissions"),
              url: `/${effectiveClientId}/qualifications`,
              icon: FileTextIcon,
              badge: badges.submissions,
              badgeTone: "warning" as const,
              badgeLabel: t("badges.pendingSubmissions", {
                count: badges.submissions,
              }),
            },
          hasPermission("calls:view") && {
            title: t("menu.callsHistory"),
            url: `/${effectiveClientId}/calls`,
            icon: ClockCounterClockwiseIcon,
          },
        ].filter(Boolean),
        [t("sections.resources")]: [
          hasPermission("settings:members") && {
            title: t("menu.team"),
            url: `/${effectiveClientId}/settings/members`,
            icon: UsersIcon,
          },
          hasPermission("schedules:view") && {
            title: t("menu.schedules"),
            url: `/${effectiveClientId}/settings/schedules`,
            icon: AlarmIcon,
          },
          hasPermission("services:view") &&
            calendarEnabled && {
              title: t("menu.services"),
              url: `/${effectiveClientId}/settings/services`,
              icon: TagIcon,
            },
          hasPermission("qualifications:view") &&
            qualificationsEnabled && {
              title: t("menu.forms"),
              url: `/${effectiveClientId}/settings/qualification-forms`,
              icon: NotepadIcon,
            },
          hasPermission("resources:view") &&
            ragSearchEnabled && {
              title: t("menu.resources"),
              url: `/${effectiveClientId}/data`,
              icon: FileTextIcon,
            },
        ].filter(Boolean),
        [t("sections.settings")]: [
          hasPermission("agents:view") && {
            title: t("menu.agents"),
            url: `/${effectiveClientId}/agents`,
            icon: UserIcon,
          },
          hasPermission("settings:tools") && {
            title: t("menu.tools"),
            url: `/${effectiveClientId}/settings/tools`,
            icon: ToolboxIcon,
          },
          // One entry for the whole company: it opens the first page the
          // member may see and stays lit on every one of them. Its pages
          // (general, users, notifications, external accounts, API) unfold
          // under it, indented, only while one of them is open — a second
          // sidebar beside the page was one rail too many.
          companyPages.length > 0 && {
            title: t("menu.company"),
            url: companyPages[0].url,
            icon: BuildingIcon,
            activePaths: companyPages.map((page) => page.url),
          },
          ...(inCompany
            ? companyPages.map((page) => ({ title: page.title, url: page.url }))
            : []),
        ].filter(Boolean),
        // Telephony, separated from the rest of the settings: numbers, channels
        // and the VoIP handsets are one subject with its own hardware and its
        // own failure modes, and lumping them under "Paramètres" buried them
        // next to the company address.
        [t("sections.channels")]: [
          hasPermission("settings:connectors") && {
            title: t("menu.channels"),
            url: `/${effectiveClientId}/settings/connectors`,
            icon: PlugsIcon,
            badge: badges.channels,
            badgeTone: "danger" as const,
            badgeLabel: t("badges.numberRequestsBlocked", {
              count: badges.channels,
            }),
          },
          voipAccountsVisible && {
            title: t("menu.voipAccounts"),
            url: `/${effectiveClientId}/settings/voip-accounts`,
            icon: DeviceMobileIcon,
          },
        ].filter(Boolean),
        // The cross-tenant back-office is its own application now — reached
        // from the user menu, not from this rail. A tenant sidebar that also
        // carried platform-wide pages made "where am I" a question, and the
        // back-office's own sidebar lists the same subjects with room to spare.
      }
    : {};

  // Filter out empty sections
  const filteredData = Object.fromEntries(
    Object.entries(data as Record<string, NavItem[]>).filter(
      ([, items]) => items.length > 0,
    ),
  ) as Record<string, NavItem[]>;

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="gap-3">
        <div className="px-2 pt-2">
          <img
            src="/logo/logo-on-white.svg"
            alt="HeyUp"
            className="h-8 w-auto dark:hidden"
          />
          <img
            src="/logo/logo-on-black.svg"
            alt="HeyUp"
            className="hidden h-8 w-auto dark:block"
          />
        </div>
        <CompanySwitcher />
      </SidebarHeader>
      <SidebarContent>
        {Object.keys(filteredData).length > 0 ? (
          Object.entries(filteredData).map(([label, items]) => (
            <SidebarGroup key={label} className="py-0">
              {label && <SidebarGroupLabel>{label}</SidebarGroupLabel>}
              <SidebarGroupContent>
                <NavSecondary items={items} />
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        ) : (
          <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
            {t("companySwitcher.noCompanyMessage")}
          </div>
        )}
      </SidebarContent>
      <SidebarFooter>
        <FirstSteps />
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}

function CompanySwitcher() {
  const { t } = useTranslation("sidebar");
  const { company: clientId } = useParams<{ company: string }>();
  const { preferredCompanyId } = usePreferredCompany();
  const { data: clients = [], isLoading } = useMyClients();

  // Use the URL company ID if available, otherwise fall back to preferred company
  const effectiveClientId = clientId || preferredCompanyId;

  const currentClient = clients.find((c) => c.client.id === effectiveClientId);

  // Nothing to switch between. Where a company-less user belongs is
  // `useCompanyRedirect`'s call (BackofficeLayout) — it knows the pages that
  // are legitimately company-less, `/companies` first of all, which is where
  // one is created. Redirecting from here reloaded the whole app out of them.
  if (!clients?.length && !isLoading) return null;

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="border border-sidebar-border bg-sidebar-accent shadow-xs hover:bg-sidebar-accent/80 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <CompanyAvatar
                logo={currentClient?.client.logo || null}
                name={currentClient?.client.name || ""}
                size="md"
              />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {currentClient?.client.name ||
                    t("companySwitcher.selectCompany")}
                </span>
                <span className="text-muted-foreground truncate text-xs">
                  {currentClient?.membership.role
                    ? t(`roles.${currentClient.membership.role}`, {
                        defaultValue: currentClient.membership.role,
                      })
                    : ""}
                </span>
              </div>
              <CaretUpDownIcon className="ml-auto opacity-60" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <DropdownMenuLabel>
              {t("companySwitcher.companies")}
            </DropdownMenuLabel>
            {withoutDemoSpaces(clients).map(({ client, membership }) => (
              <DropdownMenuItem asChild key={client.id}>
                <Link to={`/${client.id}/home`} className="gap-2 p-2">
                  <CompanyAvatar
                    logo={client.logo}
                    name={client.name}
                    size="sm"
                  />
                  <div className="flex-1">
                    <div className="font-medium">{client.name}</div>
                    <div className="text-muted-foreground text-xs">
                      {membership.role}
                    </div>
                  </div>
                </Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/companies">
                <BuildingIcon weight="fill" />
                {t("companySwitcher.manageCompanies")}
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function NavUser() {
  const { t } = useTranslation("sidebar");
  const { data: user } = useUser();
  const { data: clients = [] } = useMyClients();
  const { company: clientId } = useParams<{ company: string }>();
  const { logout } = useAuth();
  const { isMobile } = useSidebar();
  const { theme, setTheme } = useTheme();
  if (!user?.id) return <></>;
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-slate-500/10 data-[state=open]:text-sidebar-accent-foreground"
            >
              <UserAvatar
                avatar={user!.preferences.avatar}
                name={getUserName(user!)}
                email={user!.email}
                size="md"
                className="rounded-lg"
              />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {getUserName(user!)}
                </span>
                <span className="text-muted-foreground truncate text-xs">
                  {user!.email}
                </span>
              </div>
              <DotsThreeOutlineVerticalIcon
                weight="fill"
                className="ml-auto size-4"
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <UserAvatar
                  avatar={user!.preferences?.avatar}
                  name={getUserName(user!)}
                  email={user!.email}
                  size="md"
                  className="rounded-lg"
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {getUserName(user!)}
                  </span>
                  <span className="text-muted-foreground truncate text-xs">
                    {user!.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? (
                <SunIcon weight="fill" />
              ) : (
                <MoonIcon weight="fill" />
              )}
              {theme === "dark"
                ? t("userMenu.lightMode")
                : t("userMenu.darkMode")}
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/preferences">
                <GearIcon weight="fill" />
                {t("userMenu.preferences")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/account">
                <UserCircleIcon weight="fill" />
                {t("userMenu.security")}
              </Link>
            </DropdownMenuItem>
            {/* The way into the platform back-office. Staff only (the
                read-only account included), and the app behind it re-checks
                the role — this entry is a door, not a permission. */}
            {backOfficeAccess(user?.role) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/admin/live">
                    <StackIcon weight="fill" />
                    {t("userMenu.platformAdmin")}
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("userMenu.companies")}</DropdownMenuLabel>
            {clients.map(({ client }) => (
              <DropdownMenuItem key={client.id} asChild>
                <Link
                  to={`/${client.id}/home`}
                  className={
                    clientId === client.id ? "bg-slate-500/25" : undefined
                  }
                >
                  <CompanyAvatar
                    logo={client.logo}
                    name={client.name}
                    size="xs"
                  />
                  {client.name}
                </Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem asChild>
              <Link to="/companies">
                <BuildingIcon weight="fill" />
                {t("userMenu.manageCompanies")}
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logout()} className="text-red-500">
              <SignOutIcon weight="fill" />
              {t("userMenu.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** Tone of a sidebar count badge — how loudly it asks for attention. */
export type NavItemBadgeTone = "warning" | "danger";

export type NavItem = {
  title: string;
  url: string;
  icon?: any;
  /** Number of items waiting; the badge is hidden when 0 or absent. */
  badge?: number;
  badgeTone?: NavItemBadgeTone;
  /** Accessible description of what the count means. */
  badgeLabel?: string;
  /** Further URLs the entry stands for, when it opens a group of pages. */
  activePaths?: string[];
};

// `text-white!` because SidebarMenuBadge recolours itself on hover/active via
// peer variants, which would otherwise win over a plain utility class.
const badgeToneClasses: Record<NavItemBadgeTone, string> = {
  warning: "bg-amber-500 text-white!",
  danger: "bg-red-500 text-white!",
};

export function NavSecondary({
  items,
  ...props
}: {
  items: NavItem[];
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const { pathname, search } = useLocation();

  if (props.title) {
    return (
      <SidebarGroup {...props}>
        <SidebarGroupContent>
          <NavSecondary items={items} />
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  const isItemActive = (item: NavItem) => {
    const [itemPath, itemQuery] = item.url.split("?");
    const currentQuery = search.slice(1); // Remove the '?' prefix

    // If the item has a query parameter, check both path and query
    if (itemQuery) {
      return pathname === itemPath && currentQuery === itemQuery;
    }

    // For regular items, check if path starts with the item URL
    return [item.url, ...(item.activePaths || [])].some((url) =>
      pathname.startsWith(url),
    );
  };

  return (
    <SidebarMenu>
      {items.map((item) => {
        const isActive = isItemActive(item);
        const isSub = !item.icon;

        return (
          <SidebarMenuItem key={item.url}>
            <SidebarMenuButton
              asChild
              isActive={isActive}
              className={twMerge(
                isSub &&
                  "pl-8 text-[13px] h-7 data-[active=true]:bg-transparent data-[active=true]:shadow-none data-[active=true]:font-semibold data-[active=true]:text-primary-strong",
              )}
            >
              <Link to={item.url}>
                {!isSub && item.icon && <item.icon weight="fill" />}
                <span>{item.title}</span>
              </Link>
            </SidebarMenuButton>
            {!!item.badge && (
              <SidebarMenuBadge
                aria-label={item.badgeLabel}
                className={twMerge(
                  "rounded-full px-1.5 text-[11px] leading-none",
                  badgeToneClasses[item.badgeTone || "warning"],
                )}
              >
                {item.badge > 99 ? "99+" : item.badge}
              </SidebarMenuBadge>
            )}
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}
